import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FileText, ShieldCheck, Database, Users, AlertCircle, Lock, Trash2, Server } from 'lucide-react';
import Footer from '@/components/Footer';

export default function AVV() {
  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <div className="flex-grow max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="avv-page">
        <div className="mb-8">
          <Link to="/" className="text-blue-600 hover:text-blue-700 mb-4 inline-block">
            ← Zurück
          </Link>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            Auftragsverarbeitungsvertrag (AVV)
          </h1>
          <p className="text-gray-600">
            Vereinbarung gemäß Art. 28 Abs. 3 DSGVO zwischen Nutzer (Verantwortlicher) und ivoliq (Auftragsverarbeiter)
          </p>
          <p className="text-sm text-gray-500 mt-2">
            <strong>Stand:</strong> Februar 2026 &middot; <strong>Version:</strong> 1.0
          </p>
          <div className="mt-4 flex gap-3">
            <a
              href="/api/legal/avv-pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors text-sm font-medium"
              data-testid="avv-download-pdf"
            >
              <FileText className="h-4 w-4" />
              Als PDF herunterladen
            </a>
          </div>
        </div>

        <div className="space-y-6">
          {/* Präambel */}
          <Card className="rounded-xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-blue-600" />
                Präambel
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-gray-700 text-sm leading-relaxed">
              <p>
                Diese Vereinbarung regelt die Rechte und Pflichten der Parteien im Rahmen der
                Verarbeitung personenbezogener Daten, die der Auftragsverarbeiter (ivoliq) im Auftrag
                des Verantwortlichen (Nutzer) durchführt.
              </p>
              <p>
                <strong>Parteien:</strong>
              </p>
              <ul className="list-disc pl-6">
                <li>
                  <strong>Auftragsverarbeiter:</strong> ivoliq GmbH, FN 680579 b (Handelsgericht
                  Wien), vertreten durch den Geschäftsführer Wilhelm Wagner, Raffelspergergasse
                  11/2/2, 1190 Wien, Österreich (nachfolgend &bdquo;ivoliq&ldquo; oder
                  &bdquo;Auftragsverarbeiter&ldquo;)
                </li>
                <li>
                  <strong>Verantwortlicher:</strong> Der bei ivoliq registrierte Nutzer, der die
                  Plattform zur Verarbeitung personenbezogener Daten seiner Kunden,
                  Geschäftspartner oder Mitarbeiter einsetzt (nachfolgend &bdquo;Nutzer&ldquo; oder
                  &bdquo;Verantwortlicher&ldquo;)
                </li>
              </ul>
            </CardContent>
          </Card>

          {/* 1 Gegenstand und Dauer */}
          <Card className="rounded-xl">
            <CardHeader>
              <CardTitle>§ 1 Gegenstand und Dauer der Verarbeitung</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-gray-700 text-sm leading-relaxed">
              <p>
                <strong>(1) Gegenstand:</strong> Gegenstand der Vereinbarung ist die
                Verarbeitung personenbezogener Daten im Rahmen der Bereitstellung der
                SaaS-Plattform &bdquo;ivoliq&ldquo; für die Bereiche Zeiterfassung, Rechnungsstellung,
                Belegmanagement, Finanzübersicht sowie Kollaboration mit Steuerberatern.
              </p>
              <p>
                <strong>(2) Dauer:</strong> Die Vereinbarung gilt für die Dauer des
                Nutzungsverhältnisses zwischen ivoliq und dem Nutzer und endet automatisch mit der
                Beendigung des Hauptvertrags (AGB) bzw. der Löschung des Nutzerkontos.
              </p>
            </CardContent>
          </Card>

          {/* 2 Art und Zweck */}
          <Card className="rounded-xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="h-5 w-5 text-blue-600" />
                § 2 Art, Zweck und Umfang der Verarbeitung
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-gray-700 text-sm leading-relaxed">
              <p>
                <strong>(1) Art der Verarbeitung:</strong> Erheben, Speichern, Verändern,
                Übermitteln (an Steuerberater), Abrufen, Exportieren (CSV/DATEV/ZIP), Löschen.
              </p>
              <p>
                <strong>(2) Zweck:</strong> Verarbeitung im Umfang der vom Nutzer gebuchten
                Leistungen (Erstellung von Rechnungen, Speicherung und OCR-Extraktion von Belegen,
                Zeiterfassung, Buchhaltungsübergabe an Steuerberater).
              </p>
              <p>
                <strong>(3) Ort der Verarbeitung:</strong> Die Daten werden ausschließlich in der
                Europäischen Union (Deutschland – Hetzner Online GmbH, Falkenstein/Nürnberg)
                verarbeitet. Eine Übermittlung in Drittstaaten findet nicht statt, sofern nicht
                ausdrücklich in Anhang 2 genannt.
              </p>
            </CardContent>
          </Card>

          {/* 3 Art der Daten */}
          <Card className="rounded-xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5 text-blue-600" />
                § 3 Art der Daten und Kategorien betroffener Personen
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-gray-700 text-sm leading-relaxed">
              <p>
                <strong>(1) Art der personenbezogenen Daten:</strong>
              </p>
              <ul className="list-disc pl-6">
                <li>Stammdaten (Name, Anschrift, E-Mail, Telefon)</li>
                <li>Vertrags- und Rechnungsdaten (Rechnungsnummern, Beträge, Leistungen, Zahlungsinformationen)</li>
                <li>Bankverbindungsdaten (IBAN, BIC) in Rechnungen und Belegen</li>
                <li>Steueridentifikationsmerkmale (UID/USt-IdNr., Steuernummer)</li>
                <li>Kommunikationsdaten (E-Mail-Inhalte, Kommentare zwischen Nutzer und Steuerberater)</li>
                <li>Belegdaten (gescannte/fotografierte Rechnungen und Quittungen inkl. darauf enthaltener personenbezogener Daten)</li>
              </ul>
              <p>
                <strong>(2) Kategorien betroffener Personen:</strong>
              </p>
              <ul className="list-disc pl-6">
                <li>Kunden des Nutzers (natürliche Personen oder Ansprechpartner bei juristischen Personen)</li>
                <li>Lieferanten und Geschäftspartner des Nutzers</li>
                <li>Mitarbeiter des Nutzers (bei Zeiterfassung)</li>
                <li>Der eingeladene Steuerberater des Nutzers</li>
              </ul>
              <p>
                <strong>(3) Besondere Kategorien:</strong> Es werden grundsätzlich <em>keine</em> besonderen
                Kategorien personenbezogener Daten im Sinne des Art. 9 DSGVO verarbeitet. Sollten solche
                Daten dennoch in Belegen enthalten sein (z.&nbsp;B. Gesundheitsrechnungen), liegt deren
                rechtmäßige Verarbeitung in der ausschließlichen Verantwortung des Nutzers.
              </p>
            </CardContent>
          </Card>

          {/* 4 Pflichten Auftragsverarbeiter */}
          <Card className="rounded-xl">
            <CardHeader>
              <CardTitle>§ 4 Pflichten des Auftragsverarbeiters</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-gray-700 text-sm leading-relaxed">
              <ol className="list-decimal pl-6 space-y-1">
                <li>Verarbeitung ausschließlich auf dokumentierte Weisung des Verantwortlichen (inkl. dieser Vereinbarung).</li>
                <li>Verpflichtung aller zur Verarbeitung befugten Personen auf Vertraulichkeit gem. Art. 28 Abs. 3 lit. b DSGVO.</li>
                <li>Umsetzung der nach Art. 32 DSGVO erforderlichen technischen und organisatorischen Maßnahmen (siehe Anhang 1).</li>
                <li>Unterstützung des Verantwortlichen bei der Wahrnehmung der Betroffenenrechte (Art. 15–22 DSGVO) durch entsprechende Export- und Löschfunktionen.</li>
                <li>Unterstützung bei Meldepflichten gem. Art. 33, 34 DSGVO und bei einer Datenschutz-Folgenabschätzung (Art. 35 DSGVO).</li>
                <li>Unverzügliche Mitteilung an den Verantwortlichen bei Kenntnis von Datenschutzverletzungen, spätestens innerhalb von 72 Stunden.</li>
                <li>Benennung eines Ansprechpartners für Datenschutz: <a className="text-blue-600 hover:underline" href="mailto:datenschutz@ivoliq.io" data-testid="avv-contact-email">datenschutz@ivoliq.io</a></li>
                <li>Bereitstellung aller zum Nachweis der Einhaltung der Pflichten aus Art. 28 DSGVO erforderlichen Informationen.</li>
              </ol>
            </CardContent>
          </Card>

          {/* 5 Weisungsrecht */}
          <Card className="rounded-xl">
            <CardHeader>
              <CardTitle>§ 5 Weisungsrecht des Verantwortlichen</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-gray-700 text-sm leading-relaxed">
              <p>
                <strong>(1)</strong> Der Verantwortliche behält sich ein umfassendes Weisungsrecht vor.
                Weisungen erfolgen grundsätzlich in Textform (E-Mail an
                <a className="text-blue-600 hover:underline" href="mailto:datenschutz@ivoliq.io"> datenschutz@ivoliq.io</a>)
                oder durch Nutzung der in der Plattform bereitgestellten Funktionen (z.&nbsp;B. Export, Löschung).
              </p>
              <p>
                <strong>(2)</strong> Hält der Auftragsverarbeiter eine Weisung für rechtswidrig, teilt er dies
                dem Verantwortlichen unverzüglich mit und ist berechtigt, die Ausführung der betroffenen Weisung
                auszusetzen, bis sie bestätigt oder geändert wird.
              </p>
            </CardContent>
          </Card>

          {/* 6 Subunternehmer */}
          <Card className="rounded-xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Server className="h-5 w-5 text-blue-600" />
                § 6 Unterauftragsverarbeiter
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-gray-700 text-sm leading-relaxed">
              <p>
                <strong>(1)</strong> Der Verantwortliche erteilt seine allgemeine Genehmigung zum Einsatz der in
                Anhang 2 aufgeführten Unterauftragsverarbeiter.
              </p>
              <p>
                <strong>(2)</strong> ivoliq verpflichtet alle Unterauftragsverarbeiter vertraglich zu einem dem
                Schutzniveau dieser Vereinbarung gleichwertigen Datenschutzniveau (Art. 28 Abs. 4 DSGVO). Für
                Übermittlungen an Unterauftragsverarbeiter in Drittländern werden EU-Standardvertragsklauseln
                (SCC) oder vergleichbare Garantien eingesetzt.
              </p>
              <p>
                <strong>(3)</strong> Beabsichtigte Änderungen der Unterauftragsverarbeiter werden dem
                Verantwortlichen mindestens 30 Tage im Voraus per E-Mail mitgeteilt. Der Verantwortliche hat das
                Recht, einer Änderung aus wichtigem Grund zu widersprechen.
              </p>
            </CardContent>
          </Card>

          {/* 7 Kontrollrechte */}
          <Card className="rounded-xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lock className="h-5 w-5 text-blue-600" />
                § 7 Kontrollrechte und Nachweise
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-gray-700 text-sm leading-relaxed">
              <p>
                <strong>(1)</strong> Der Verantwortliche ist berechtigt, die Einhaltung der Vereinbarung zu
                kontrollieren. Kontrollen finden in der Regel durch Einsichtnahme in Zertifikate, Gutachten und
                schriftliche Selbstauskünfte statt.
              </p>
              <p>
                <strong>(2)</strong> ivoliq stellt auf Anfrage folgende Nachweise bereit: Dokumentation der TOMs
                (Anhang 1), aktuelle Liste der Unterauftragsverarbeiter (Anhang 2), Informationen zu Serverstandorten
                und relevante Zertifikate der Sub-Processors (z.&nbsp;B. ISO 27001 von Hetzner).
              </p>
              <p>
                <strong>(3)</strong> Vor-Ort-Kontrollen sind mit einer Vorlaufzeit von mindestens 30 Tagen und
                gegen Übernahme angemessener Kosten möglich.
              </p>
            </CardContent>
          </Card>

          {/* 8 Datenpannen */}
          <Card className="rounded-xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-orange-600" />
                § 8 Meldung von Datenschutzverletzungen
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-gray-700 text-sm leading-relaxed">
              <p>
                Bei jeder Datenschutzverletzung informiert ivoliq den Verantwortlichen unverzüglich, spätestens
                jedoch innerhalb von 72 Stunden nach Kenntnisnahme, per E-Mail. Die Meldung enthält die in Art. 33
                Abs. 3 DSGVO vorgesehenen Informationen.
              </p>
            </CardContent>
          </Card>

          {/* 9 Löschung */}
          <Card className="rounded-xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Trash2 className="h-5 w-5 text-red-600" />
                § 9 Löschung und Rückgabe der Daten
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-gray-700 text-sm leading-relaxed">
              <p>
                <strong>(1)</strong> Nach Beendigung des Vertragsverhältnisses werden alle personenbezogenen Daten
                nach Wahl des Verantwortlichen zurückgegeben (Export als CSV/DATEV/ZIP über die integrierte
                Exportfunktion) oder innerhalb von 30 Tagen gelöscht.
              </p>
              <p>
                <strong>(2)</strong> Gesetzliche Aufbewahrungspflichten (z.&nbsp;B. für steuerlich relevante
                Unterlagen nach § 147 AO / § 132 BAO / Art. 958f OR) bleiben unberührt.
              </p>
              <p>
                <strong>(3)</strong> Backups werden spätestens nach 90 Tagen rotiert und überschrieben.
              </p>
            </CardContent>
          </Card>

          {/* 10 Haftung */}
          <Card className="rounded-xl">
            <CardHeader>
              <CardTitle>§ 10 Haftung</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-gray-700 text-sm leading-relaxed">
              <p>
                Die Haftung der Parteien richtet sich nach den Bestimmungen des Hauptvertrags (AGB) sowie nach
                Art. 82 DSGVO.
              </p>
            </CardContent>
          </Card>

          {/* 11 Schlussbestimmungen */}
          <Card className="rounded-xl">
            <CardHeader>
              <CardTitle>§ 11 Schlussbestimmungen</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-gray-700 text-sm leading-relaxed">
              <p>
                <strong>(1)</strong> Änderungen dieser Vereinbarung bedürfen der Textform. Bei Widersprüchen
                zwischen dieser Vereinbarung und anderen Regelungen gehen die Bestimmungen dieser Vereinbarung
                vor, soweit die Datenverarbeitung betroffen ist.
              </p>
              <p>
                <strong>(2)</strong> Es gilt österreichisches Recht unter Ausschluss des UN-Kaufrechts.
                Gerichtsstand ist Wien, Österreich, soweit gesetzlich zulässig.
              </p>
              <p>
                <strong>(3)</strong> Sollte eine Bestimmung unwirksam sein, bleibt die Wirksamkeit der übrigen
                Bestimmungen unberührt.
              </p>
            </CardContent>
          </Card>

          {/* Anhang 1 – TOMs */}
          <Card className="rounded-xl">
            <CardHeader>
              <CardTitle>Anhang 1 – Technische und organisatorische Maßnahmen (TOMs)</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-gray-700 text-sm leading-relaxed">
              <div>
                <p className="font-semibold">1. Vertraulichkeit (Art. 32 Abs. 1 lit. b DSGVO)</p>
                <ul className="list-disc pl-6">
                  <li><strong>Zutrittskontrolle:</strong> ISO 27001-zertifizierte Rechenzentren (Hetzner Falkenstein/Nürnberg) mit 24/7-Zutrittskontrolle, Videoüberwachung, Zweifaktor-Zutritt.</li>
                  <li><strong>Zugangskontrolle:</strong> Starke Passwort-Hashing (bcrypt), Zwei-Faktor-Authentifizierung für Admin-Zugänge, Session-Management mit Inaktivitäts-Timeout.</li>
                  <li><strong>Zugriffskontrolle:</strong> Rollenbasiertes Berechtigungskonzept (Nutzer/Steuerberater/Admin), Trennung der Daten auf Userebene (Row-Level-Isolation).</li>
                  <li><strong>Trennungskontrolle:</strong> Logische Mandantentrennung in der Datenbank durch User-IDs; dedizierte Storage-Namespaces pro Nutzer.</li>
                </ul>
              </div>
              <div>
                <p className="font-semibold">2. Integrität (Art. 32 Abs. 1 lit. b DSGVO)</p>
                <ul className="list-disc pl-6">
                  <li><strong>Weitergabekontrolle:</strong> Alle Übertragungen über TLS 1.2+ (HTTPS). Object Storage mit SSE-Option (AES-256).</li>
                  <li><strong>Eingabekontrolle:</strong> Revisionssichere Audit-Logs für sicherheitsrelevante Aktionen.</li>
                </ul>
              </div>
              <div>
                <p className="font-semibold">3. Verfügbarkeit und Belastbarkeit (Art. 32 Abs. 1 lit. b DSGVO)</p>
                <ul className="list-disc pl-6">
                  <li>Tägliche automatische Backups, georedundant; Aufbewahrung 30 Tage.</li>
                  <li>Monitoring und Alerting (Uptime &gt; 99,5&nbsp;%).</li>
                  <li>DDoS-Schutz auf Infrastrukturebene.</li>
                </ul>
              </div>
              <div>
                <p className="font-semibold">4. Verfahren zur regelmäßigen Überprüfung (Art. 32 Abs. 1 lit. d DSGVO)</p>
                <ul className="list-disc pl-6">
                  <li>Datenschutz-Management durch internen Ansprechpartner.</li>
                  <li>Incident-Response-Prozess mit dokumentierter Meldekette.</li>
                  <li>Regelmäßige Sicherheitsupdates und Dependency-Scans.</li>
                  <li>Auftragskontrolle durch schriftliche AVV mit allen Sub-Processors.</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Anhang 2 – Sub-Processors */}
          <Card className="rounded-xl">
            <CardHeader>
              <CardTitle>Anhang 2 – Unterauftragsverarbeiter (Sub-Processors)</CardTitle>
            </CardHeader>
            <CardContent className="text-sm">
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-100 text-left">
                      <th className="p-2 border">Dienstleister</th>
                      <th className="p-2 border">Zweck</th>
                      <th className="p-2 border">Standort</th>
                      <th className="p-2 border">Garantien</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="p-2 border">Hetzner Online GmbH</td>
                      <td className="p-2 border">Hosting, Object Storage, Datenbank</td>
                      <td className="p-2 border">DE (Falkenstein / Nürnberg)</td>
                      <td className="p-2 border">ISO 27001, AVV</td>
                    </tr>
                    <tr>
                      <td className="p-2 border">Stripe Payments Europe Ltd.</td>
                      <td className="p-2 border">Zahlungsabwicklung (Abonnements)</td>
                      <td className="p-2 border">IE / EU</td>
                      <td className="p-2 border">DPA, SCC</td>
                    </tr>
                    <tr>
                      <td className="p-2 border">Sendinblue SAS (Brevo)</td>
                      <td className="p-2 border">Transaktionale E-Mails</td>
                      <td className="p-2 border">FR / EU</td>
                      <td className="p-2 border">DPA</td>
                    </tr>
                    <tr>
                      <td className="p-2 border">Google Ireland Ltd.</td>
                      <td className="p-2 border">OAuth-Login (optional), Gmail-Versand (opt-in)</td>
                      <td className="p-2 border">IE / EU</td>
                      <td className="p-2 border">DPA, SCC</td>
                    </tr>
                    <tr>
                      <td className="p-2 border">Microsoft Ireland / Microsoft Corp.</td>
                      <td className="p-2 border">Microsoft Clarity (Heatmaps & Session Recordings, nur nach Consent)</td>
                      <td className="p-2 border">IE / US</td>
                      <td className="p-2 border">DPA, EU-US DPF</td>
                    </tr>
                    <tr>
                      <td className="p-2 border">PostHog Inc.</td>
                      <td className="p-2 border">Produkt- und Web-Analytics auf EU-Servern (nur nach Consent)</td>
                      <td className="p-2 border">US / EU</td>
                      <td className="p-2 border">DPA, SCC</td>
                    </tr>
                    <tr>
                      <td className="p-2 border">Cal.com, Inc.</td>
                      <td className="p-2 border">Terminbuchung (nur für Demo-Buchungen)</td>
                      <td className="p-2 border">US</td>
                      <td className="p-2 border">SCC</td>
                    </tr>
                    <tr>
                      <td className="p-2 border">OpenAI, Inc.</td>
                      <td className="p-2 border">Optionale KI-Belegerkennung / OCR-Erweiterung (nur mit Einwilligung)</td>
                      <td className="p-2 border">US</td>
                      <td className="p-2 border">SCC, EU-US DPF</td>
                    </tr>
                    <tr>
                      <td className="p-2 border">Anthropic, PBC</td>
                      <td className="p-2 border">Optionale KI-Belegerkennung / OCR-Erweiterung (nur mit Einwilligung)</td>
                      <td className="p-2 border">US</td>
                      <td className="p-2 border">SCC</td>
                    </tr>
                    <tr>
                      <td className="p-2 border">Emergent Labs, Inc.</td>
                      <td className="p-2 border">Fallback Object Storage, LLM für OCR</td>
                      <td className="p-2 border">US / EU</td>
                      <td className="p-2 border">SCC, Opt-in</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="mt-3 text-xs text-gray-600">
                Die aktuelle Liste wird regelmäßig aktualisiert. Wesentliche Änderungen werden dem Nutzer 30 Tage
                im Voraus per E-Mail mitgeteilt.
              </p>
            </CardContent>
          </Card>

          <Card className="rounded-xl bg-blue-50 border-blue-200">
            <CardContent className="pt-6 text-sm text-gray-700">
              <p className="font-semibold mb-2">Annahme dieser Vereinbarung</p>
              <p>
                Mit der Registrierung eines ivoliq-Kontos bzw. der Bestätigung in den
                Konto-Einstellungen stimmen Sie dieser Auftragsverarbeitungsvereinbarung zu. Auf
                Wunsch erhalten Sie eine signierte PDF-Kopie per E-Mail. Fragen richten Sie bitte an
                <a className="text-blue-600 hover:underline" href="mailto:datenschutz@ivoliq.io" data-testid="avv-final-contact-email"> datenschutz@ivoliq.io</a>.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
      <Footer />
    </div>
  );
}
